import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { api } from '../services/api.ts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { User, Mail, Phone, Shield, Bell, Heart, Loader2, X, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    interests: user?.interests?.join(', ') || '',
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.two_factor_enabled || false);
  const [twoFactorMethod, setTwoFactorMethod] = useState<'totp' | 'sms'>(user?.two_factor_method || 'totp');
  const [notifications, setNotifications] = useState({
    eventReminders: true,
    matchNotifications: true,
    chatMessages: true,
    friendRequests: true,
  });

  // 2FA States
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAPassword, setTwoFAPassword] = useState('');
  const [twoFAQRCode, setTwoFAQRCode] = useState<string | null>(null);
  const [twoFASecret, setTwoFASecret] = useState<string | null>(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [smsPhone, setSmsPhone] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [is2FALoading, setIs2FALoading] = useState(false);

  // Password States
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        bio: user.bio || '',
        interests: user.interests?.join(', ') || '',
      });
      setTwoFactorEnabled(user.two_factor_enabled || false);
      setTwoFactorMethod(user.two_factor_method || 'totp');
    }
  }, [user]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const interests = formData.interests.split(',').map(i => i.trim()).filter(Boolean);
      await updateUser({
        name: formData.name,
        bio: formData.bio,
        interests,
      });
      toast.success('Profile updated successfully');
      setIsEditing(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Start 2FA Setup
  const handleStart2FASetup = () => {
    setShow2FASetup(true);
    setTwoFAPassword('');
    setTwoFAQRCode(null);
    setTwoFASecret(null);
    setTwoFACode('');
    setSmsPhone('');
    setSmsSent(false);
  };

  // Setup TOTP 2FA
  const handleSetupTOTP = async () => {
    if (!twoFAPassword) {
      toast.error('Please enter your password');
      return;
    }
    setIs2FALoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/enable-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: twoFAPassword })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to setup 2FA');
      
      setTwoFAQRCode(data.qr_code);
      setTwoFASecret(data.secret);
      toast.success('Scan the QR code with your authenticator app');
    } catch (error: any) {
      toast.error(error.message || 'Failed to setup 2FA');
    } finally {
      setIs2FALoading(false);
    }
  };

  // Confirm TOTP 2FA
  const handleConfirmTOTP = async () => {
    if (!twoFACode) {
      toast.error('Please enter the 6-digit code from your authenticator');
      return;
    }
    setIs2FALoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/confirm-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: twoFACode })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to confirm 2FA');
      
      setTwoFactorEnabled(true);
      setShow2FASetup(false);
      toast.success('2FA enabled successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Invalid code');
    } finally {
      setIs2FALoading(false);
    }
  };

  // Setup SMS 2FA - Send Code
  const handleSetupSMS = async () => {
    if (!twoFAPassword) {
      toast.error('Please enter your password');
      return;
    }
    if (!smsPhone) {
      toast.error('Please enter your phone number');
      return;
    }
    setIs2FALoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/2fa/setup-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: twoFAPassword, phone_number: smsPhone })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to send SMS');
      
      setSmsSent(true);
      toast.success('Verification code sent to your phone');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send SMS');
    } finally {
      setIs2FALoading(false);
    }
  };

  // Confirm SMS 2FA
  const handleConfirmSMS = async () => {
    if (!twoFACode) {
      toast.error('Please enter the verification code');
      return;
    }
    setIs2FALoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/2fa/confirm-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: twoFACode })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to verify code');
      
      setTwoFactorEnabled(true);
      setTwoFactorMethod('sms');
      setShow2FASetup(false);
      toast.success('SMS 2FA enabled successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Invalid code');
    } finally {
      setIs2FALoading(false);
    }
  };

  // Disable 2FA
  const handleDisable2FA = async () => {
    if (!twoFACode) {
      toast.error('Please enter your 2FA code to disable');
      return;
    }
    setIs2FALoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/disable-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: twoFACode })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to disable 2FA');
      
      setTwoFactorEnabled(false);
      setTwoFACode('');
      toast.success('2FA disabled successfully');
    } catch (error: any) {
      toast.error(error.message || 'Invalid code');
    } finally {
      setIs2FALoading(false);
    }
  };

  // Change Password
  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setIsChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to change password');
      
      setShowPasswordChange(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleToggle2FA = async () => {
    if (!twoFactorEnabled) {
      handleStart2FASetup();
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="space-y-4">
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
          Profile
        </h1>
        <p className="text-lg text-gray-400">Manage your account and preferences</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="glass border-white/10">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6 space-y-6">
          <Card className="glass border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Personal Information</CardTitle>
              {!isEditing ? (
                <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
                  Edit Profile
                </Button>
              ) : (
                <div className="flex space-x-2">
                  <Button onClick={() => setIsEditing(false)} variant="ghost" size="sm">
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={isSaving} size="sm">
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Profile Picture */}
              <div className="flex items-center space-x-4">
                <div className="h-20 w-20 rounded-full bg-primary/20 flex items-center justify-center">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-20 w-20 rounded-full object-cover" />
                  ) : (
                    <User className="h-10 w-10 text-primary" />
                  )}
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl">{user.name}</h3>
                  <p className="text-sm text-gray-400">{user.email}</p>
                  {user.vibe_score && (
                    <Badge variant="secondary" className="mt-1">
                      Vibe Score: {user.vibe_score}%
                    </Badge>
                  )}
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={!isEditing}
                    className="glass border-white/10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      value={user.email}
                      disabled
                      className="glass border-white/10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <Input
                      id="phone"
                      value={user.phone_number || 'Not provided'}
                      disabled
                      className="glass border-white/10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    disabled={!isEditing}
                    placeholder="Tell us about yourself..."
                    className="glass border-white/10 min-h-[100px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interests">Interests (comma-separated)</Label>
                  <Input
                    id="interests"
                    value={formData.interests}
                    onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
                    disabled={!isEditing}
                    placeholder="Music, Dancing, Art, Technology"
                    className="glass border-white/10"
                  />
                  {user.interests && user.interests.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {user.interests.map((interest) => (
                        <Badge key={interest} variant="outline">
                          <Heart className="h-3 w-3 mr-1" />
                          {interest}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="mt-6 space-y-6">
          {/* 2FA Setup Modal */}
          {show2FASetup && (
            <Card className="glass border-primary/50 relative">
              <button
                onClick={() => setShow2FASetup(false)}
                className="absolute top-4 right-4 p-1 hover:bg-white/10 rounded"
              >
                <X className="h-5 w-5" />
              </button>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <span>Setup Two-Factor Authentication</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Method Selection */}
                <div className="space-y-3">
                  <Label>Choose 2FA Method</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                      type="button"
                      className={`p-4 rounded-lg border text-left transition-all ${
                        twoFactorMethod === 'totp'
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                      onClick={() => { setTwoFactorMethod('totp'); setSmsSent(false); setTwoFAQRCode(null); }}
                    >
                      <p className="font-medium">Authenticator App</p>
                      <p className="text-sm text-gray-400">Google Authenticator, Authy, etc.</p>
                    </button>
                    <button
                      type="button"
                      className={`p-4 rounded-lg border text-left transition-all ${
                        twoFactorMethod === 'sms'
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                      onClick={() => { setTwoFactorMethod('sms'); setTwoFAQRCode(null); }}
                    >
                      <p className="font-medium">SMS</p>
                      <p className="text-sm text-gray-400">Receive codes via text</p>
                    </button>
                  </div>
                </div>

                {/* Password Input (required for both methods) */}
                {!twoFAQRCode && !smsSent && (
                  <div className="space-y-3">
                    <Label htmlFor="2fa-password">Enter your password to continue</Label>
                    <Input
                      id="2fa-password"
                      type="password"
                      value={twoFAPassword}
                      onChange={(e) => setTwoFAPassword(e.target.value)}
                      placeholder="Your current password"
                      className="glass border-white/10"
                    />
                    {twoFactorMethod === 'sms' && (
                      <div className="space-y-2">
                        <Label htmlFor="sms-phone">Phone Number</Label>
                        <Input
                          id="sms-phone"
                          type="tel"
                          value={smsPhone}
                          onChange={(e) => setSmsPhone(e.target.value)}
                          placeholder="+1234567890"
                          className="glass border-white/10"
                        />
                      </div>
                    )}
                    <Button
                      onClick={twoFactorMethod === 'totp' ? handleSetupTOTP : handleSetupSMS}
                      disabled={is2FALoading || !twoFAPassword || (twoFactorMethod === 'sms' && !smsPhone)}
                      className="w-full"
                    >
                      {is2FALoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {twoFactorMethod === 'totp' ? 'Generate QR Code' : 'Send Verification Code'}
                    </Button>
                  </div>
                )}

                {/* TOTP QR Code Display */}
                {twoFactorMethod === 'totp' && twoFAQRCode && (
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-400 mb-4">
                        Scan this QR code with your authenticator app
                      </p>
                      <div className="inline-block p-4 bg-white rounded-lg">
                        <img src={twoFAQRCode} alt="2FA QR Code" className="w-48 h-48" />
                      </div>
                      {twoFASecret && (
                        <p className="mt-4 text-xs text-gray-500">
                          Manual entry code: <code className="bg-white/10 px-2 py-1 rounded">{twoFASecret}</code>
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="totp-code">Enter 6-digit code from your app</Label>
                      <Input
                        id="totp-code"
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        className="glass border-white/10 text-center text-2xl tracking-widest"
                      />
                    </div>
                    <Button
                      onClick={handleConfirmTOTP}
                      disabled={is2FALoading || twoFACode.length !== 6}
                      className="w-full"
                    >
                      {is2FALoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Verify & Enable 2FA
                    </Button>
                  </div>
                )}

                {/* SMS Code Input */}
                {twoFactorMethod === 'sms' && smsSent && (
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-400">
                        A verification code has been sent to {smsPhone}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sms-code">Enter verification code</Label>
                      <Input
                        id="sms-code"
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        className="glass border-white/10 text-center text-2xl tracking-widest"
                      />
                    </div>
                    <Button
                      onClick={handleConfirmSMS}
                      disabled={is2FALoading || twoFACode.length !== 6}
                      className="w-full"
                    >
                      {is2FALoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Verify & Enable 2FA
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => { setSmsSent(false); setTwoFACode(''); }}
                      className="w-full"
                    >
                      Resend Code
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* 2FA Status Card */}
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-primary" />
                <span>Two-Factor Authentication</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-medium">2FA Status</p>
                  <p className="text-sm text-gray-400">
                    {twoFactorEnabled 
                      ? `Enabled via ${twoFactorMethod === 'totp' ? 'Authenticator App' : 'SMS'}`
                      : 'Add an extra layer of security to your account'}
                  </p>
                </div>
                {twoFactorEnabled ? (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Enabled</Badge>
                ) : (
                  <Badge variant="outline" className="text-gray-400">Disabled</Badge>
                )}
              </div>

              {!twoFactorEnabled && !show2FASetup && (
                <Button onClick={handleStart2FASetup} className="w-full">
                  <Shield className="h-4 w-4 mr-2" />
                  Setup 2FA
                </Button>
              )}

              {twoFactorEnabled && (
                <div className="space-y-3 pt-4 border-t border-white/10">
                  <Label>Enter your 2FA code to disable</Label>
                  <Input
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="glass border-white/10"
                  />
                  <Button
                    variant="destructive"
                    onClick={handleDisable2FA}
                    disabled={is2FALoading || twoFACode.length !== 6}
                    className="w-full"
                  >
                    {is2FALoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Disable 2FA
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
            </CardHeader>
            <CardContent>
              {!showPasswordChange ? (
                <Button 
                  variant="outline" 
                  className="glass-hover"
                  onClick={() => setShowPasswordChange(true)}
                >
                  Update Password
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current-password">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="current-password"
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        className="glass border-white/10 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        className="glass border-white/10 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirm-password"
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        className="glass border-white/10 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setShowPasswordChange(false);
                        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleChangePassword}
                      disabled={isChangingPassword}
                      className="flex-1"
                    >
                      {isChangingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Change Password
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-6 space-y-6">
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="h-5 w-5 text-primary" />
                <span>Notification Preferences</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Event Reminders</p>
                  <p className="text-sm text-gray-400">Get notified before your events start</p>
                </div>
                <Switch
                  checked={notifications.eventReminders}
                  onCheckedChange={(checked) =>
                    setNotifications({ ...notifications, eventReminders: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div>
                  <p className="font-medium">Vibe Match Notifications</p>
                  <p className="text-sm text-gray-400">When you get new vibe matches</p>
                </div>
                <Switch
                  checked={notifications.matchNotifications}
                  onCheckedChange={(checked) =>
                    setNotifications({ ...notifications, matchNotifications: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div>
                  <p className="font-medium">Chat Messages</p>
                  <p className="text-sm text-gray-400">New messages in event chats</p>
                </div>
                <Switch
                  checked={notifications.chatMessages}
                  onCheckedChange={(checked) =>
                    setNotifications({ ...notifications, chatMessages: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div>
                  <p className="font-medium">Friend Requests</p>
                  <p className="text-sm text-gray-400">When someone sends you a friend request</p>
                </div>
                <Switch
                  checked={notifications.friendRequests}
                  onCheckedChange={(checked) =>
                    setNotifications({ ...notifications, friendRequests: checked })
                  }
                />
              </div>

              <Button className="w-full mt-6 shadow-glow">
                Save Notification Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
